import React, { useState, useMemo } from 'react';
import { useLang } from '../i18n/useLang';

const CAL_URL = 'https://cal.com/willy-carlos-tchuitcheu-lz7ww7/15min?overlayCalendar=true';

// Tag keys are always English — labels shown in UI come from translations
const FAQ_DATA = [
	// Clients
	{
		key: 'faq-1',
		tagKeys: ['Clients'],
		qKey: 'q1',
		aKey: 'a1',
	},
	{
		key: 'faq-2',
		tagKeys: ['Clients'],
		qKey: 'q2',
		aKey: 'a2',
	},
	{
		key: 'faq-3',
		tagKeys: ['Clients', 'Process'],
		qKey: 'q3',
		aKey: 'a3',
	},
	// Services
	{
		key: 'faq-4',
		tagKeys: ['Services'],
		qKey: 'q4',
		aKey: 'a4',
	},
	{
		key: 'faq-5',
		tagKeys: ['Services', 'Process'],
		qKey: 'q5',
		aKey: 'a5',
	},
	{
		key: 'faq-6',
		tagKeys: ['Services'],
		qKey: 'q6',
		aKey: 'a6',
	},
	// Process
	{
		key: 'faq-7',
		tagKeys: ['Process'],
		qKey: 'q7',
		aKey: 'a7',
	},
	{
		key: 'faq-8',
		tagKeys: ['Process'],
		qKey: 'q8',
		aKey: 'a8',
	},
	{
		key: 'faq-9',
		tagKeys: ['Process'],
		qKey: 'q9',
		aKey: 'a9',
	},
	// Pricing
	{
		key: 'faq-10',
		tagKeys: ['Pricing'],
		qKey: 'q10',
		aKey: 'a10',
	},
	{
		key: 'faq-11',
		tagKeys: ['Pricing'],
		qKey: 'q11',
		aKey: 'a11',
	},
	{
		key: 'faq-12',
		tagKeys: ['Pricing', 'Research'],
		qKey: 'q12',
		aKey: 'a12',
	},
	// Technology
	{
		key: 'faq-13',
		tagKeys: ['Technology'],
		qKey: 'q13',
		aKey: 'a13',
	},
	{
		key: 'faq-14',
		tagKeys: ['Technology'],
		qKey: 'q14',
		aKey: 'a14',
	},
	// Africa
	{
		key: 'faq-15',
		tagKeys: ['Africa'],
		qKey: 'q15',
		aKey: 'a15',
	},
	{
		key: 'faq-16',
		tagKeys: ['Africa'],
		qKey: 'q16',
		aKey: 'a16',
	},
	// Research
	{
		key: 'faq-17',
		tagKeys: ['Research'],
		qKey: 'q17',
		aKey: 'a17',
	},
	{
		key: 'faq-18',
		tagKeys: ['Research'],
		qKey: 'q18',
		aKey: 'a18',
	},
];

// Map tag key → accent colour classes for the tag chip
const TAG_COLORS = {
	Clients: { bg: 'bg-nf-teal/10', text: 'text-nf-teal', border: 'border-nf-teal/20' },
	Services: { bg: 'bg-nf-cyan/10', text: 'text-nf-cyan', border: 'border-nf-cyan/20' },
	Process: { bg: 'bg-nf-p3/10', text: 'text-nf-p3', border: 'border-nf-p3/20' },
	Pricing: { bg: 'bg-nf-amber/10', text: 'text-nf-amber', border: 'border-nf-amber/20' },
	Technology: { bg: 'bg-nf-rose/10', text: 'text-nf-rose', border: 'border-nf-rose/20' },
	Africa: { bg: 'bg-nf-green/10', text: 'text-nf-green', border: 'border-nf-green/20' },
	Research: { bg: 'bg-purple-500/10', text: 'text-purple-400', border: 'border-purple-500/20' },
};

export default function FAQ({ navigate }) {
	const { t, lang } = useLang();
	const filterKeys = ['All', ...t('faq.filterKeys')];
	const filterLabels = [t('faq.filterAll'), ...t('faq.filterLabels')];

	const [activeFilter, setActiveFilter] = useState('All');
	const [openIndex, setOpenIndex] = useState(null);
	const [searchQuery, setSearchQuery] = useState('');

	// Build a map from tagKey → translated label
	const tagLabelMap = {};
	t('faq.filterKeys').forEach((k, i) => { tagLabelMap[k] = t('faq.filterLabels')[i]; });

	// Relevance scoring: question matches worth 3×, answer matches worth 1×
	const scoreItem = (item, terms) => {
		const q = t(`faq.${item.qKey}`).toLowerCase();
		const a = t(`faq.${item.aKey}`).toLowerCase();
		let score = 0;
		terms.forEach(term => {
			if (q.includes(term)) score += 3;
			if (a.includes(term)) score += 1;
		});
		return score;
	};

	const filtered = useMemo(() => {
		const tagFiltered = activeFilter === 'All'
			? FAQ_DATA
			: FAQ_DATA.filter(item => item.tagKeys.includes(activeFilter));
		const q = searchQuery.trim().toLowerCase();
		if (!q) return tagFiltered;
		const terms = q.split(/\s+/).filter(Boolean);
		return tagFiltered
			.map(item => ({ ...item, _score: scoreItem(item, terms) }))
			.filter(item => item._score > 0)
			.sort((a, b) => b._score - a._score);
	}, [activeFilter, searchQuery, lang]);

	const toggle = (key) => setOpenIndex(prev => (prev === key ? null : key));

	return (
		<div>
			{/* Hero */}
			<div className="pt-[72px] sm:pt-[80px] pb-10 lg:pb-14 px-4 sm:px-8 lg:px-20 text-center relative overflow-hidden">
				<div className="absolute inset-0 pointer-events-none"
					style={{ background: 'radial-gradient(ellipse 60% 55% at 50% 25%, rgba(109,40,217,.13) 0%, transparent 65%)' }} />
				<div className="relative max-w-[820px] mx-auto">
					<span className="sec-label">{t('faq.secLabel')}</span>
					<h1
						className="font-heading font-black leading-[1.05] tracking-[-0.03em] mt-4 mb-4"
						style={{ fontSize: 'clamp(28px, 4vw, 56px)' }}>
						{t('faq.h1')[0]}<br />
						<span className="text-nf-p3">{t('faq.h1')[1]}</span>
					</h1>
					<p className="text-[15px] sm:text-[16px] text-nf-t2 max-w-[560px] mx-auto mb-6 leading-relaxed">
						{t('faq.heroBody')}
					</p>

					{/* Search input */}
					<div className="relative max-w-[520px] mx-auto mb-6">
						<span className="absolute left-4 top-1/2 -translate-y-1/2 text-nf-t3 text-[15px] pointer-events-none">🔍</span>
						<input
							type="text"
							value={searchQuery}
							onChange={e => { setSearchQuery(e.target.value); setOpenIndex(null); }}
							placeholder={t('faq.searchPlaceholder')}
							className="w-full bg-nf-bg3 border border-white/[0.08] rounded-xl pl-10 pr-9 py-3 text-[14px] text-nf-text placeholder-nf-t3 focus:outline-none focus:border-[rgba(109,40,217,0.4)] transition-colors"
						/>
						{searchQuery && (
							<button
								onClick={() => { setSearchQuery(''); setOpenIndex(null); }}
								className="absolute right-3 top-1/2 -translate-y-1/2 text-nf-t3 hover:text-nf-t2 text-xl leading-none cursor-pointer"
								aria-label="Clear search">
								×
							</button>
						)}
					</div>

					{/* Filter pills */}
					<div className="flex flex-wrap justify-center gap-2">
						{filterKeys.map((key, i) => (
							<button
								key={key}
								onClick={() => { setActiveFilter(key); setOpenIndex(null); }}
								className={`px-4 py-2 rounded-full text-sm border transition-all cursor-pointer ${activeFilter === key
									? 'bg-nf-p/20 border-[rgba(109,40,217,0.4)] text-nf-acc'
									: 'bg-transparent border-white/[0.08] text-nf-t2 hover:border-white/[0.2] hover:text-nf-text'
									}`}>
								{filterLabels[i]}
							</button>
						))}
					</div>
				</div>
			</div>

			{/* Accordion Q&A */}
			<section className="px-4 sm:px-8 lg:px-20 pb-16 lg:pb-24">
				<div className="max-w-[820px] mx-auto">
					{filtered.length === 0 && (
						<p className="text-center text-nf-t3 py-12">{t('faq.noResults')}</p>
					)}
					<div className="flex flex-col gap-3">
						{filtered.map((item, idx) => {
							const question = t(`faq.${item.qKey}`);
							const answer = t(`faq.${item.aKey}`);
							const isOpen = openIndex === item.key || (searchQuery.trim() && idx === 0 && openIndex === null);

							return (
								<div
									key={item.key}
									className={`bg-nf-bg3 border rounded-2xl overflow-hidden transition-all duration-200 ${isOpen ? 'border-[rgba(109,40,217,0.3)]' : 'border-white/[0.06] hover:border-white/[0.1]'
										}`}>
									{/* Question row */}
									<button
										className="w-full flex items-start justify-between gap-4 px-5 sm:px-6 py-5 text-left cursor-pointer"
										onClick={() => toggle(item.key)}
										aria-expanded={isOpen}>
										<div className="flex-1 min-w-0">
											{/* Tag chips */}
											<div className="flex flex-wrap gap-1.5 mb-3">
												{item.tagKeys.map(tk => {
													const c = TAG_COLORS[tk] || { bg: 'bg-white/5', text: 'text-nf-t2', border: 'border-white/10' };
													return (
														<span
															key={tk}
															className={`text-[11px] font-semibold px-2.5 py-[3px] rounded-full border ${c.bg} ${c.text} ${c.border}`}>
															{tagLabelMap[tk] || tk}
														</span>
													);
												})}
											</div>
											<span className="font-heading font-bold text-[15px] sm:text-[16px] text-nf-text leading-snug">
												{question}
											</span>
										</div>
										<span
											className={`shrink-0 mt-1 w-6 h-6 flex items-center justify-center rounded-full border text-nf-t2 text-[12px] transition-all duration-200 ${isOpen ? 'rotate-180 border-[rgba(109,40,217,0.35)] text-nf-acc' : 'border-white/[0.1]'
												}`}>
											▾
										</span>
									</button>

									{/* Answer */}
									{isOpen && (
										<div className="px-5 sm:px-6 pb-6 pt-0">
											<div className="border-t border-white/[0.06] pt-4">
												<p className="text-[14px] sm:text-[15px] text-nf-t2 leading-[1.75]">{answer}</p>
											</div>
										</div>
									)}
								</div>
							);
						})}
					</div>
				</div>
			</section>

			{/* CTA */}
			<section className="px-4 sm:px-8 lg:px-20 pb-20 lg:pb-28">
				<div className="max-w-[660px] mx-auto text-center bg-nf-bg3 border border-white/[0.07] rounded-3xl px-6 sm:px-12 py-10 sm:py-14"
					style={{ background: 'linear-gradient(135deg, rgba(109,40,217,0.07) 0%, rgba(6,182,212,0.04) 100%)' }}>
					<h2 className="font-heading font-black text-[22px] sm:text-[28px] mb-3 leading-tight">
						{t('faq.cta.heading')}
					</h2>
					<p className="text-[14px] sm:text-[15px] text-nf-t2 mb-7 max-w-[420px] mx-auto leading-relaxed">
						{t('faq.cta.body')}
					</p>
					<a
						href={CAL_URL}
						target="_blank"
						rel="noopener noreferrer"
						className="btn-cta inline-block">
						{t('faq.cta.btn')}
					</a>
				</div>
			</section>
		</div>
	);
}
